/**
 * Icon — FontAwesome wrapper for React Native
 *
 * Mirrors the web app's <FontAwesomeIcon> usage pattern.
 * Requires loadIcons() to have been called at app startup.
 *
 * USAGE — two equivalent styles:
 *   <Icon icon={['fad', 'sparkles']} size={20} color={theme.colors.primary} />
 *   <Icon icon={['fas', 'chevron-right']} size={14} color={theme.colors.muted} />
 *   // Convenience form — `name` is shorthand for ['fas', name]:
 *   <Icon name="chevron-right" size={14} color={theme.colors.muted} />
 *
 * The string `name` form is shorthand that always resolves to the solid
 * (`fas`) variant. Pass an explicit tuple to use `fad`/`far`/`fal`/`fab`.
 * If the resolved icon isn't registered in the loadIcons() bundle, the
 * IconErrorBoundary falls back to an invisible same-size View so layout
 * stays correct without crashing the screen.
 */

import React from 'react';
import { View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { findIconDefinition, type IconProp, type IconPrefix, type IconName } from '@fortawesome/fontawesome-svg-core';

// Try a list of prefixes for a given icon name and return the first one
// that's actually registered in the library. Lets the `name` shorthand
// fall back from 'fas' → 'fad' → 'far' instead of throwing when only one
// variant has been imported. Returns null if none match.
function pickRegisteredPrefix(name: string): IconPrefix | null {
  const tryPrefixes: IconPrefix[] = ['fas', 'fad', 'far', 'fal', 'fab'];
  for (const prefix of tryPrefixes) {
    if (findIconDefinition({ prefix, iconName: name as IconName })) return prefix;
  }
  return null;
}

interface IconProps {
  /** Full FontAwesome tuple, e.g. `['fas', 'chevron-right']` or `['fad', 'sparkles']`. */
  icon?: IconProp;
  /** Convenience: shorthand for `['fas', name]`. Ignored when `icon` is supplied. */
  name?: string;
  size?: number;
  color?: string;
  style?: object;
  dataSet?: Record<string, string>;
}

class IconErrorBoundary extends React.Component<{ size: number; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) {
      // Render an invisible placeholder of the same size so layout is preserved
      return <View style={{ width: this.props.size, height: this.props.size }} />;
    }
    return this.props.children;
  }
}

const Icon = ({ icon, name, size = 16, color, style, dataSet }: IconProps) => {
  // Resolve the FontAwesome lookup tuple.
  //
  //   - Explicit tuple `icon`: try the requested prefix first, then fall
  //     back through the other prefixes if the requested one isn't
  //     registered. Lets a screen pass `['fas', 'home']` and still render
  //     when only the duotone is in the library.
  //   - Shorthand `name`: walk fas → fad → far → fal → fab and pick the
  //     first one registered.
  //   - If nothing is registered for the icon name under ANY prefix, render
  //     a blank placeholder of the right size so layout is preserved
  //     without spamming "icon not found" warnings from FontAwesomeIcon.
  let resolvedIcon: IconProp | null = null;
  if (Array.isArray(icon) && icon.length === 2) {
    const [reqPrefix, iconName] = icon as [IconPrefix, IconName];
    if (iconName) {
      if (findIconDefinition({ prefix: reqPrefix, iconName })) {
        resolvedIcon = icon;
      } else {
        const fallback = pickRegisteredPrefix(iconName);
        if (fallback) resolvedIcon = [fallback, iconName] as unknown as IconProp;
      }
    }
  } else if (icon) {
    resolvedIcon = icon;
  } else if (name) {
    const prefix = pickRegisteredPrefix(name);
    if (prefix) resolvedIcon = [prefix, name] as unknown as IconProp;
  }

  if (!resolvedIcon) {
    return <View {...({ dataSet } as any)} style={[{ width: size, height: size }, style as any]} />;
  }
  return (
    <IconErrorBoundary size={size}>
      <FontAwesomeIcon
        icon={resolvedIcon as IconProp}
        size={size}
        color={color}
        style={style as any}
        {...({ dataSet } as any)}
      />
    </IconErrorBoundary>
  );
};

export default Icon;
